import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, ArrowRight, Sparkles, Plus, Trash2, GripVertical, Save,
    Play, Wand2, CheckCircle2, AlertTriangle, Lock, Terminal,
    Users, Database, Wrench, Shield, ClipboardCopy,
} from "lucide-react";
import axios from "axios";
import api from "@/lib/api";
import { readSseLines } from "@/lib/sse";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
    { key: "basics",       label: "1. Basics",              icon: Wand2 },
    { key: "eligibility",  label: "2. Eligibility",         icon: Users },
    { key: "inputs",       label: "3. Required Inputs",     icon: Plus },
    { key: "instructions", label: "4. Instructions",        icon: Sparkles },
    { key: "data",         label: "5. Data & Actions",      icon: Database },
    { key: "approval",     label: "6. Approval & Memory",   icon: Shield },
    { key: "test",         label: "7. Test",                icon: Terminal },
    { key: "publish",      label: "8. Publish",             icon: CheckCircle2 },
];

const CATEGORIES = ["executive", "legal", "sales", "hr", "ops", "finance", "marketing", "engineering", "other"];

const INPUT_TYPES = [
    { value: "string", label: "Short text"   },
    { value: "text",   label: "Long text"    },
    { value: "number", label: "Number"       },
    { value: "date",   label: "Date"         },
    { value: "select", label: "Select"       },
    { value: "file",   label: "File upload"  },
];

const DEFAULT_ACTIONS_ALLOWED = ["generate_document"];
const ALL_ACTIONS = [
    "generate_document", "send_email_draft", "create_task", "search_knowledge",
    "query_integration", "call_ai_agent", "escalate_to_human",
];
const PROHIBITED_DEFAULTS = ["send_external_email", "make_payment", "file_court_document", "sign_contract"];

const AGENT_ROLES = [
    "Legal Assistant", "AI Legal Assistant",
    "Contract Attorney", "AI Contract Attorney",
    "Data Analyst", "Recruiter", "Executive Assistant",
    "Customer Support", "Content Writer",
    "AI CFO", "AI COO", "AI Marketing Director", "AI Sales Manager",
    "AI SDR", "AI Account Executive", "AI HR Director", "AI Accountant",
    "AI Procurement Officer", "AI Compliance Officer", "AI Cybersecurity Analyst",
    "AI Software Engineer", "AI QA Engineer", "AI DevOps Engineer",
    "AI Social Media Manager", "AI Graphic Designer",
    "Chief of Staff", "AI Chief of Staff", "Meeting Assistant",
];

const EMPTY_FORM = {
    name: "",
    description: "",
    category: "executive",
    icon: "",
    eligible_agent_roles: [],
    eligible_agent_slugs: [],
    required_inputs: [],
    instructions: "",
    data_sources: {
        conversation_memory: false, user_memory: false, company_memory: false,
        matter_memory: false, knowledge_base: false, integrations: [],
    },
    actions_allowed: [...DEFAULT_ACTIONS_ALLOWED],
    actions_prohibited: [...PROHIBITED_DEFAULTS],
    approval_required: false,
    approval_role: "any_user",
    output_format: "markdown",
    memory_rules: {
        remember: false, retention_days: 90, scope: "conversation", is_privileged: false,
    },
    visibility: "private",
    monthly_price_if_public: null,
    testing_examples: [],
};

export default function SkillBuilder() {
    const nav = useNavigate();
    const { skillId } = useParams();
    const [step, setStep] = useState(0);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [drafting, setDrafting] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [authAllowed, setAuthAllowed] = useState(null); // null=loading, true=ok, false=locked
    const instructionsRef = useRef(null);

    // Sprint J-3-G · gate the wizard behind skill_author. Locked users are
    // redirected back to /app/skills with a toast.
    useEffect(() => {
        api.get("/access/summary").then(({ data }) => {
            const canAuthor = !!data.owner || data?.usage?.skill_author?.cap === null;
            if (!canAuthor) {
                setAuthAllowed(false);
                toast.error("Skill authoring requires a paid plan.");
                nav("/app/skills", { replace: true });
            } else {
                setAuthAllowed(true);
            }
        }).catch(() => setAuthAllowed(true)); // fail-open on unrelated errors
    }, [nav]);

    const isSeed = !!form.is_system_seed;
    const canDelete = !!skillId && !isSeed;

    const confirmDelete = async () => {
        if (!skillId) return;
        setDeleting(true);
        try {
            await api.delete(`/custom-skills/${skillId}`);
            toast.success("Skill deleted.");
            setDeleteOpen(false);
            nav("/app/skills");
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Delete failed.");
        } finally {
            setDeleting(false);
        }
    };

    useEffect(() => {
        if (!skillId) return;
        (async () => {
            try {
                const { data } = await api.get(`/custom-skills/${skillId}`);
                // Merge with EMPTY_FORM so any missing fields default to sane values.
                setForm({
                    ...EMPTY_FORM,
                    ...data,
                    _id: data.id,
                    slug: data.slug,
                    data_sources: { ...EMPTY_FORM.data_sources, ...(data.data_sources || {}) },
                    memory_rules: { ...EMPTY_FORM.memory_rules, ...(data.memory_rules || {}) },
                });
            } catch (e) {
                toast.error(e.response?.data?.detail || "Could not load skill.");
            }
        })();
    }, [skillId]);

    /* -------- test-run state (Step 7) -------- */
    const [testInputs, setTestInputs] = useState({});
    const [testOutput, setTestOutput] = useState("");
    const [testRunning, setTestRunning] = useState(false);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const setDS = (patch) => setForm((f) => ({ ...f, data_sources: { ...f.data_sources, ...patch } }));
    const setMem = (patch) => setForm((f) => ({ ...f, memory_rules: { ...f.memory_rules, ...patch } }));

    const canGoNext = useMemo(() => {
        if (step === 0) return form.name.trim().length >= 2;
        if (step === 3) return form.instructions.trim().length >= 20;
        return true;
    }, [step, form]);

    const goNext = () => {
        if (!canGoNext) { toast.error("Please complete the required fields."); return; }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };
    const goPrev = () => setStep((s) => Math.max(s - 1, 0));

    const draftWithAI = async () => {
        if (!form.name.trim()) {
            toast.error("Add a skill name first.");
            return;
        }
        setDrafting(true);
        try {
            const { data } = await api.post("/custom-skills/draft-instructions", {
                name: form.name,
                description: form.description,
                category: form.category,
                required_inputs: form.required_inputs,
            });
            set({ instructions: data.instructions || "" });
            toast.success("Draft instructions generated. Edit as needed.");
        } catch (e) {
            toast.error(e.response?.data?.detail || "AI draft failed.");
        } finally {
            setDrafting(false);
        }
    };

    const insertVarAtCursor = (key) => {
        const el = instructionsRef.current;
        if (!el) { set({ instructions: (form.instructions || "") + `{{${key}}}` }); return; }
        const start = el.selectionStart ?? form.instructions.length;
        const end   = el.selectionEnd   ?? form.instructions.length;
        const val   = form.instructions || "";
        const next  = val.slice(0, start) + `{{${key}}}` + val.slice(end);
        set({ instructions: next });
        setTimeout(() => {
            const pos = start + key.length + 4;
            el.focus();
            el.setSelectionRange(pos, pos);
        }, 0);
    };

    const runTest = async () => {
        // Requires the skill to exist server-side. If we haven't saved yet, save as draft first.
        setTestRunning(true);
        setTestOutput("");
        try {
            let skillId = form._id;
            if (!skillId) {
                const { data } = await api.post("/custom-skills", form);
                skillId = data.id;
                set({ _id: data.id, slug: data.slug });
            } else {
                await api.patch(`/custom-skills/${skillId}`, form);
            }
            const token = localStorage.getItem("wfos_token");
            const resp = await fetch(`${API_BASE}/custom-skills/${skillId}/test-run`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ inputs: testInputs }),
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            let acc = "";
            let hadError = false;
            for await (const j of readSseLines(resp)) {
                if (j.type === "delta" && j.content) {
                    acc += j.content;
                    setTestOutput(acc);
                } else if (j.type === "done" && j.output) {
                    acc = j.output;
                    setTestOutput(acc);
                } else if (j.type === "error") {
                    hadError = true;
                    toast.error(j.message || "Test run failed.");
                }
            }
            if (!hadError) toast.success("Test run complete.");
        } catch (e) {
            toast.error(e.message || "Test run failed.");
        } finally {
            setTestRunning(false);
        }
    };

    const saveAsExample = () => {
        if (!testOutput) return;
        const name = prompt("Name this test case:", `Example ${(form.testing_examples || []).length + 1}`);
        if (!name) return;
        set({
            testing_examples: [
                ...(form.testing_examples || []),
                { name, inputs: testInputs, expected_output_snippet: testOutput.slice(0, 200) },
            ],
        });
        toast.success("Saved as testing example.");
    };

    const publish = async ({ activate }) => {
        setSaving(true);
        try {
            let skillId = form._id;
            const payload = { ...form };
            delete payload._id;
            if (skillId) {
                await api.patch(`/custom-skills/${skillId}`, payload);
            } else {
                const { data } = await api.post("/custom-skills", payload);
                skillId = data.id;
            }
            if (activate) await api.post(`/custom-skills/${skillId}/activate`);
            toast.success(activate ? "Skill activated." : "Draft saved.");
            nav("/app/skills");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Save failed.");
        } finally {
            setSaving(false);
        }
    };

    /* ============================== RENDER ============================== */
    // Sprint J-3-G · while access is being resolved, or if the user is locked
    // and being redirected, render nothing (avoid a flash of the wizard shell).
    if (authAllowed !== true) {
        return (
            <div className="h-full grid place-items-center text-sm"
                 style={{ color: "var(--atlas-text-muted)" }}
                 data-testid="skill-builder-locked">
                {authAllowed === false ? "Redirecting…" : "Checking access…"}
            </div>
        );
    }
    return (
        <div className="h-full flex overflow-hidden" data-testid="skill-builder-page">
            {/* LEFT — step progress */}
            <aside
                className="w-[240px] flex-shrink-0 overflow-y-auto atlas-scrollbar"
                style={{
                    background: "var(--atlas-gradient-nav)",
                    borderRight: "1px solid var(--atlas-border)",
                }}
            >
                <div className="p-5">
                    <Link
                        to="/app/skills"
                        className="inline-flex items-center gap-1 text-[12px] transition-colors hover:opacity-100 opacity-80"
                        style={{ color: "var(--atlas-text-tertiary)" }}
                        data-testid="skill-builder-back"
                    >
                        <ArrowLeft className="w-3 h-3" strokeWidth={2} /> Skill Builder
                    </Link>
                    <div className="mt-5 atlas-hero-eyebrow" data-testid="skill-builder-eyebrow">
                        <Sparkles className="w-3 h-3" strokeWidth={2.5} />
                        {skillId ? "Edit skill" : "Build a skill"}
                    </div>
                    <div className="mt-4 space-y-1">
                        {STEPS.map((s, i) => {
                            const Icon = s.icon;
                            const active = i === step;
                            const done = i < step;
                            return (
                                <button
                                    key={s.key}
                                    onClick={() => setStep(i)}
                                    data-testid={`skill-step-${s.key}`}
                                    className="w-full text-left px-3 py-2 rounded-lg text-[12.5px] flex items-center gap-2 transition-all"
                                    style={
                                        active
                                            ? {
                                                  background: "linear-gradient(90deg, rgba(139,92,246,0.22) 0%, rgba(139,92,246,0.06) 100%)",
                                                  color: "var(--atlas-text-primary)",
                                                  boxShadow: "inset 2px 0 0 var(--atlas-primary), 0 0 18px -6px var(--atlas-primary-glow)",
                                              }
                                            : done
                                                ? { color: "var(--atlas-text-secondary)" }
                                                : { color: "var(--atlas-text-muted)" }
                                    }
                                >
                                    <Icon
                                        className="w-3.5 h-3.5"
                                        strokeWidth={2}
                                        style={{ color: done ? "#34d399" : (active ? "var(--atlas-primary-hover)" : "currentColor") }}
                                    />
                                    {s.label}
                                    {done && (
                                        <CheckCircle2
                                            className="w-3 h-3 ml-auto"
                                            strokeWidth={2.5}
                                            style={{ color: "#34d399" }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {canDelete && (
                        <div
                            className="mt-6 pt-4"
                            style={{ borderTop: "1px solid var(--atlas-border)" }}
                        >
                            <button
                                onClick={() => setDeleteOpen(true)}
                                data-testid="skill-delete-btn"
                                className="w-full inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-[12px] font-medium transition-all"
                                style={{
                                    color: "#f87171",
                                    background: "transparent",
                                    border: "1px solid rgba(239,68,68,0.35)",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = "rgba(239,68,68,0.10)";
                                    e.currentTarget.style.borderColor = "rgba(239,68,68,0.60)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = "transparent";
                                    e.currentTarget.style.borderColor = "rgba(239,68,68,0.35)";
                                }}
                            >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                                Delete skill
                            </button>
                        </div>
                    )}
                </div>
            </aside>

            {/* CENTER — active step form */}
            <main className="flex-1 min-w-0 overflow-y-auto atlas-scrollbar">
                <div className="max-w-2xl mx-auto p-8">
                    {step === 0 && <StepBasics form={form} set={set} />}
                    {step === 1 && <StepEligibility form={form} set={set} />}
                    {step === 2 && <StepInputs form={form} set={set} />}
                    {step === 3 && (
                        <StepInstructions
                            form={form} set={set}
                            drafting={drafting}
                            onDraftWithAI={draftWithAI}
                            onInsertVar={insertVarAtCursor}
                            instructionsRef={instructionsRef}
                        />
                    )}
                    {step === 4 && <StepDataActions form={form} set={set} setDS={setDS} />}
                    {step === 5 && <StepApprovalMemory form={form} set={set} setMem={setMem} />}
                    {step === 6 && (
                        <StepTest
                            form={form}
                            testInputs={testInputs}
                            setTestInputs={setTestInputs}
                            testOutput={testOutput}
                            testRunning={testRunning}
                            onRun={runTest}
                            onSaveExample={saveAsExample}
                        />
                    )}
                    {step === 7 && <StepPublish form={form} set={set} onSave={publish} saving={saving} />}

                    {/* Nav */}
                    <div
                        className="mt-8 flex items-center justify-between pt-6"
                        style={{ borderTop: "1px solid var(--atlas-border)" }}
                    >
                        <button
                            onClick={goPrev}
                            disabled={step === 0}
                            data-testid="skill-builder-prev"
                            className="inline-flex items-center gap-1 text-[13px] transition-colors disabled:opacity-40"
                            style={{ color: "var(--atlas-text-tertiary)" }}
                        >
                            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Back
                        </button>
                        {step < STEPS.length - 1 ? (
                            <button
                                onClick={goNext}
                                disabled={!canGoNext}
                                data-testid="skill-builder-next"
                                className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-10 text-[13px]"
                            >
                                Continue <ArrowRight className="w-4 h-4" strokeWidth={2} />
                            </button>
                        ) : (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => publish({ activate: false })}
                                    disabled={saving}
                                    data-testid="skill-builder-save-draft"
                                    className="atlas-btn-ghost inline-flex items-center gap-1 rounded-lg px-3 h-10 text-[13px]"
                                >
                                    Save draft
                                </button>
                                <button
                                    onClick={() => publish({ activate: true })}
                                    disabled={saving}
                                    data-testid="skill-builder-activate"
                                    className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-10 text-[13px]"
                                >
                                    <CheckCircle2 className="w-4 h-4" strokeWidth={2.5} />
                                    {saving ? "Activating…" : "Activate skill"}
                                </button>
                            </div>
                        )}
                    </div>
                    <div className="h-16" />
                </div>
            </main>

            {/* RIGHT — live preview */}
            <aside
                className="w-[340px] flex-shrink-0 overflow-y-auto atlas-scrollbar hidden xl:block"
                style={{
                    borderLeft: "1px solid var(--atlas-border)",
                    background: "linear-gradient(180deg, rgba(10,10,26,0.5) 0%, rgba(5,5,16,0.7) 100%)",
                }}
                data-testid="skill-builder-preview"
            >
                <div className="p-5">
                    <div className="atlas-label">Live preview</div>
                    <div className="mt-3 atlas-card atlas-card--elevated p-4">
                        <div
                            className="text-[15px] font-semibold flex items-center gap-1.5"
                            style={{ color: "var(--atlas-text-primary)" }}
                        >
                            {form.icon && <span>{form.icon}</span>}
                            {form.name || (
                                <span style={{ color: "var(--atlas-text-muted)" }}>Untitled skill</span>
                            )}
                        </div>
                        <div
                            className="mt-0.5 text-[10.5px] font-semibold uppercase tracking-widest"
                            style={{ color: "var(--atlas-text-muted)" }}
                        >
                            {form.category} · draft
                        </div>
                        <p
                            className="mt-2 text-[12.5px] leading-relaxed line-clamp-3"
                            style={{ color: "var(--atlas-text-tertiary)" }}
                        >
                            {form.description || (
                                <i style={{ color: "var(--atlas-text-muted)" }}>Add a description on step 1.</i>
                            )}
                        </p>
                        {(form.eligible_agent_roles || []).length > 0 && (
                            <div className="mt-2 flex items-center gap-1 flex-wrap">
                                {form.eligible_agent_roles.slice(0, 4).map((r) => (
                                    <span
                                        key={r}
                                        className="px-1.5 py-0.5 rounded-full text-[10px]"
                                        style={{
                                            background: "var(--atlas-bg-highlight)",
                                            color: "var(--atlas-text-tertiary)",
                                            border: "1px solid var(--atlas-border-strong)",
                                        }}
                                    >
                                        {r}
                                    </span>
                                ))}
                            </div>
                        )}
                        {form.approval_required && (
                            <span className="mt-3 inline-flex items-center gap-1 atlas-pill atlas-pill--warning text-[9.5px]">
                                <Lock className="w-2.5 h-2.5" strokeWidth={2.5} /> requires {form.approval_role}
                            </span>
                        )}
                    </div>

                    <div className="mt-4 atlas-label">Snapshot</div>
                    <ul
                        className="mt-2 text-[12px] space-y-1"
                        style={{ color: "var(--atlas-text-tertiary)" }}
                    >
                        <li>Inputs: <b style={{ color: "var(--atlas-text-primary)" }}>{(form.required_inputs || []).length}</b></li>
                        <li>Actions allowed: <b style={{ color: "var(--atlas-text-primary)" }}>{(form.actions_allowed || []).length}</b></li>
                        <li>Memory: <b style={{ color: "var(--atlas-text-primary)" }}>{form.memory_rules?.remember ? form.memory_rules.scope : "off"}</b></li>
                        <li>Approval: <b style={{ color: "var(--atlas-text-primary)" }}>{form.approval_required ? form.approval_role : "none"}</b></li>
                        <li>Visibility: <b style={{ color: "var(--atlas-text-primary)" }}>{form.visibility}</b></li>
                        <li>Test examples: <b style={{ color: "var(--atlas-text-primary)" }}>{(form.testing_examples || []).length}</b></li>
                    </ul>
                </div>
            </aside>
            <ConfirmDeleteModal
                open={deleteOpen}
                title="Delete this skill?"
                body={`"${form.name || "Untitled skill"}" will be deleted and any installations on your hired Workforce Members will be uninstalled. This can't be undone.`}
                confirmLabel="Delete skill"
                onConfirm={confirmDelete}
                onClose={() => setDeleteOpen(false)}
                busy={deleting}
            />
        </div>
    );
}

/* ============================== STEP COMPONENTS ============================== */
function Field({ label, required, children, hint }) {
    return (
        <div>
            <label className="block text-[12.5px] font-medium text-gray-800 mb-1.5">
                {label} {required && <span className="text-[#dc2626]">*</span>}
            </label>
            {children}
            {hint && <div className="mt-1 text-[11px] text-gray-500">{hint}</div>}
        </div>
    );
}

function StepBasics({ form, set }) {
    return (
        <div className="space-y-5" data-testid="step-basics">
            <div>
                <h2 className="atlas-text-gradient text-[26px] font-semibold tracking-tight">Basics</h2>
                <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Give the skill a name, a short description, and a home category.
                </p>
            </div>
            <Field label="Name" required>
                <input
                    value={form.name}
                    onChange={(e) => set({ name: e.target.value })}
                    data-testid="skill-name"
                    placeholder="e.g., Quarterly OKR Summary"
                    className="w-full rounded-lg border border-[#e8e8e3] bg-white h-11 px-3 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                />
            </Field>
            <Field label="Description" hint="One-line pitch. What does this skill do?">
                <textarea
                    value={form.description}
                    onChange={(e) => set({ description: e.target.value })}
                    rows={3}
                    data-testid="skill-description"
                    placeholder="Consolidate quarterly OKR progress into a 1-page board-ready review."
                    className="w-full rounded-lg border border-[#e8e8e3] bg-white p-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a] resize-y"
                />
            </Field>
            <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                    <select
                        value={form.category}
                        onChange={(e) => set({ category: e.target.value })}
                        data-testid="skill-category-select"
                        className="w-full rounded-lg border border-[#e8e8e3] bg-white h-11 px-3 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                    >
                        {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                </Field>
                <Field label="Icon (optional)" hint="One emoji, e.g. ✍️ or 📊.">
                    <input
                        value={form.icon || ""}
                        onChange={(e) => set({ icon: e.target.value.slice(0, 4) })}
                        data-testid="skill-icon"
                        maxLength={4}
                        className="w-full rounded-lg border border-[#e8e8e3] bg-white h-11 px-3 text-[14px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                    />
                </Field>
            </div>
        </div>
    );
}

function StepEligibility({ form, set }) {
    const toggleRole = (r) => set({
        eligible_agent_roles: form.eligible_agent_roles.includes(r)
            ? form.eligible_agent_roles.filter((x) => x !== r)
            : [...form.eligible_agent_roles, r],
    });
    return (
        <div className="space-y-5" data-testid="step-eligibility">
            <div>
                <h2 className="atlas-text-gradient text-[26px] font-semibold tracking-tight">Eligibility</h2>
                <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Choose which Workforce Member roles can install this skill. Leave empty to allow all.
                </p>
            </div>
            <div className="atlas-card p-4">
                <div className="text-[12px] font-medium mb-2" style={{ color: "var(--atlas-text-secondary)" }}>
                    Eligible Workforce Member roles
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="skill-roles-pool">
                    {AGENT_ROLES.map((r) => {
                        const active = form.eligible_agent_roles.includes(r);
                        return (
                            <button
                                key={r}
                                type="button"
                                onClick={() => toggleRole(r)}
                                data-testid={`skill-role-${r.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                                    active ? "border-[#16a34a] bg-[#16a34a] text-white" : "border-[#e8e8e3] bg-white text-gray-700 hover:border-[#c7c7bd]"
                                }`}
                            >
                                {r}
                            </button>
                        );
                    })}
                </div>
                <div className="mt-2 text-[11px] text-gray-500">
                    Selected: <b>{form.eligible_agent_roles.length || "any"}</b>
                </div>
            </div>
        </div>
    );
}

function StepInputs({ form, set }) {
    const addInput = () => set({
        required_inputs: [
            ...(form.required_inputs || []),
            { key: "", label: "", type: "string", required: true, help_text: "" },
        ],
    });
    const removeInput = (idx) => set({
        required_inputs: form.required_inputs.filter((_, i) => i !== idx),
    });
    const updateInput = (idx, patch) => set({
        required_inputs: form.required_inputs.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    });

    return (
        <div className="space-y-4" data-testid="step-inputs">
            <div>
                <h2 className="atlas-text-gradient text-[26px] font-semibold tracking-tight">Required Inputs</h2>
                <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                    These are the fields the user fills in when they run this skill. Reference them in your instructions with <code>{"{{key}}"}</code>.
                </p>
            </div>
            <div className="space-y-2" data-testid="skill-inputs-list">
                {(form.required_inputs || []).map((inp, idx) => (
                    <div key={idx} className="wfos-card p-3 flex items-start gap-2" data-testid={`skill-input-row-${idx}`}>
                        <GripVertical className="w-4 h-4 text-gray-300 mt-3 flex-shrink-0" strokeWidth={2} />
                        <div className="flex-1 grid grid-cols-6 gap-2 items-start">
                            <input
                                value={inp.key}
                                onChange={(e) => updateInput(idx, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
                                placeholder="key"
                                data-testid={`skill-input-key-${idx}`}
                                className="col-span-1 rounded-md border border-[#e8e8e3] px-2 h-9 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#16a34a] font-mono"
                            />
                            <input
                                value={inp.label}
                                onChange={(e) => updateInput(idx, { label: e.target.value })}
                                placeholder="Label"
                                data-testid={`skill-input-label-${idx}`}
                                className="col-span-2 rounded-md border border-[#e8e8e3] px-2 h-9 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            />
                            <select
                                value={inp.type}
                                onChange={(e) => updateInput(idx, { type: e.target.value })}
                                data-testid={`skill-input-type-${idx}`}
                                className="col-span-1 rounded-md border border-[#e8e8e3] px-2 h-9 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            >
                                {INPUT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                            <label className="col-span-1 inline-flex items-center gap-1 text-[12px] text-gray-700 mt-2">
                                <input
                                    type="checkbox"
                                    checked={inp.required !== false}
                                    onChange={(e) => updateInput(idx, { required: e.target.checked })}
                                    data-testid={`skill-input-required-${idx}`}
                                />
                                required
                            </label>
                            <button
                                onClick={() => removeInput(idx)}
                                data-testid={`skill-input-remove-${idx}`}
                                className="col-span-1 text-rose-500 hover:text-rose-700 text-[12px] inline-flex items-center justify-end gap-1 mt-2"
                            >
                                <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                            </button>
                            <input
                                value={inp.help_text || ""}
                                onChange={(e) => updateInput(idx, { help_text: e.target.value })}
                                placeholder="Help text (optional)"
                                data-testid={`skill-input-help-${idx}`}
                                className="col-span-6 rounded-md border border-[#e8e8e3] px-2 h-8 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            />
                            {inp.type === "select" && (
                                <input
                                    value={(inp.options || []).join(", ")}
                                    onChange={(e) => updateInput(idx, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                                    placeholder="Options: comma, separated, list"
                                    data-testid={`skill-input-options-${idx}`}
                                    className="col-span-6 rounded-md border border-[#e8e8e3] px-2 h-8 text-[12px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                                />
                            )}
                        </div>
                    </div>
                ))}
                {(form.required_inputs || []).length === 0 && (
                    <div className="wfos-card p-6 text-center text-[13px] text-gray-500" data-testid="skill-inputs-empty">
                        No inputs yet. Every skill needs at least one so the AI knows what you're asking about.
                    </div>
                )}
            </div>
            <button
                onClick={addInput}
                data-testid="skill-inputs-add"
                className="inline-flex items-center gap-1 rounded-lg border border-dashed border-[#c7c7bd] px-3 py-2 text-[13px] text-gray-600 hover:border-[#16a34a] hover:text-[#16a34a]"
            >
                <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Add input
            </button>
        </div>
    );
}

function StepInstructions({ form, set, drafting, onDraftWithAI, onInsertVar, instructionsRef }) {
    const varKeys = (form.required_inputs || []).map((i) => i.key).filter(Boolean);
    return (
        <div className="space-y-4" data-testid="step-instructions">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h2 className="atlas-text-gradient text-[26px] font-semibold tracking-tight">Instructions</h2>
                    <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                        The system prompt the AI follows when running the skill. Reference inputs with <code>{"{{key}}"}</code>.
                    </p>
                </div>
                <button
                    onClick={onDraftWithAI}
                    disabled={drafting}
                    data-testid="skill-draft-with-ai"
                    className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-[12.5px]"
                >
                    <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> {drafting ? "Drafting…" : "Draft with AI"}
                </button>
            </div>
            {varKeys.length > 0 && (
                <div className="wfos-card p-3">
                    <div className="text-[11px] font-medium text-gray-500 mb-1.5">Suggested variables — click to insert at cursor:</div>
                    <div className="flex flex-wrap gap-1.5">
                        {varKeys.map((k) => (
                            <button
                                key={k}
                                type="button"
                                onClick={() => onInsertVar(k)}
                                data-testid={`skill-var-${k}`}
                                className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-800 text-[11.5px] font-mono hover:bg-slate-200"
                            >
                                {"{{"}{k}{"}}"}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            <textarea
                ref={instructionsRef}
                value={form.instructions}
                onChange={(e) => set({ instructions: e.target.value })}
                rows={16}
                data-testid="skill-instructions"
                placeholder="You are the … Follow these steps: …"
                className="w-full rounded-lg border border-[#e8e8e3] bg-white p-3 text-[13.5px] font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-[#16a34a] resize-y"
            />
            <div className="text-[11px] text-gray-500">
                Length: {(form.instructions || "").length} chars.
            </div>
        </div>
    );
}

function StepDataActions({ form, set, setDS }) {
    const toggleAct = (a, list) => {
        const cur = form[list] || [];
        set({ [list]: cur.includes(a) ? cur.filter((x) => x !== a) : [...cur, a] });
    };
    const dsToggles = [
        ["conversation_memory", "Conversation memory"],
        ["user_memory",         "User memory"],
        ["company_memory",      "Company memory"],
        ["matter_memory",       "Matter memory (legal)"],
        ["knowledge_base",      "Knowledge base (RAG)"],
    ];
    return (
        <div className="space-y-5" data-testid="step-data">
            <div>
                <h2 className="atlas-text-gradient text-[26px] font-semibold tracking-tight">Data & Actions</h2>
                <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Pick which memory pools this skill can read, and which actions it may execute.
                </p>
            </div>
            <div className="wfos-card p-4">
                <div className="text-[12px] font-medium text-gray-700 mb-2">Data sources</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {dsToggles.map(([k, label]) => (
                        <label key={k} className="inline-flex items-center gap-2 text-[13px] text-gray-800">
                            <input
                                type="checkbox"
                                checked={!!form.data_sources?.[k]}
                                onChange={(e) => setDS({ [k]: e.target.checked })}
                                data-testid={`skill-ds-${k}`}
                            />
                            {label}
                        </label>
                    ))}
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="wfos-card p-4">
                    <div className="text-[12px] font-medium text-gray-700 mb-2">Actions allowed</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="skill-actions-allowed">
                        {ALL_ACTIONS.map((a) => {
                            const active = form.actions_allowed.includes(a);
                            return (
                                <button
                                    key={a}
                                    type="button"
                                    onClick={() => toggleAct(a, "actions_allowed")}
                                    data-testid={`skill-action-allow-${a}`}
                                    className={`px-2 py-0.5 rounded-full text-[11px] font-medium border transition-colors ${
                                        active ? "border-[#16a34a] bg-[#16a34a] text-white"
                                               : "border-[#e8e8e3] bg-white text-gray-700 hover:border-[#c7c7bd]"
                                    }`}
                                >
                                    {a}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="wfos-card p-4">
                    <div className="text-[12px] font-medium text-gray-700 mb-2">Actions prohibited</div>
                    <div className="flex flex-wrap gap-1.5" data-testid="skill-actions-prohibited">
                        {PROHIBITED_DEFAULTS.map((a) => (
                            <span
                                key={a}
                                className="px-2 py-0.5 rounded-full text-[11px] font-medium border border-rose-200 bg-rose-50 text-rose-700"
                            >
                                {a}
                            </span>
                        ))}
                    </div>
                    <div className="mt-2 text-[11px] text-gray-500">
                        Prohibited actions are enforced platform-wide and cannot be removed.
                    </div>
                </div>
            </div>
        </div>
    );
}

function StepApprovalMemory({ form, set, setMem }) {
    return (
        <div className="space-y-5" data-testid="step-approval">
            <div>
                <h2 className="atlas-text-gradient text-[26px] font-semibold tracking-tight">Approval & Memory</h2>
                <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Add a human-in-the-loop for high-stakes actions, and decide what the skill remembers between runs.
                </p>
            </div>
            <div className="wfos-card p-4">
                <label className="inline-flex items-center gap-2 text-[13px] text-gray-800" data-testid="skill-approval-toggle">
                    <input
                        type="checkbox"
                        checked={form.approval_required}
                        onChange={(e) => set({ approval_required: e.target.checked })}
                    />
                    Require human approval before any external action
                </label>
                {form.approval_required && (
                    <div className="mt-3">
                        <Field label="Who approves?">
                            <select
                                value={form.approval_role}
                                onChange={(e) => set({ approval_role: e.target.value })}
                                data-testid="skill-approval-role"
                                className="rounded-lg border border-[#e8e8e3] bg-white h-10 px-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            >
                                <option value="any_user">Any user in the tenant</option>
                                <option value="admin">Admins only</option>
                                <option value="attorney">Attorneys only (legal skills)</option>
                            </select>
                        </Field>
                    </div>
                )}
            </div>
            <div className="wfos-card p-4">
                <label className="inline-flex items-center gap-2 text-[13px] text-gray-800" data-testid="skill-memory-toggle">
                    <input
                        type="checkbox"
                        checked={form.memory_rules?.remember}
                        onChange={(e) => setMem({ remember: e.target.checked })}
                    />
                    Remember information between runs
                </label>
                {form.memory_rules?.remember && (
                    <div className="mt-3 grid grid-cols-2 gap-3">
                        <Field label="Retention" hint="Days. Leave blank for permanent.">
                            <input
                                type="number"
                                min={1}
                                max={3650}
                                value={form.memory_rules?.retention_days ?? ""}
                                onChange={(e) => setMem({ retention_days: e.target.value ? Number(e.target.value) : null })}
                                data-testid="skill-memory-retention"
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white h-10 px-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            />
                        </Field>
                        <Field label="Scope">
                            <select
                                value={form.memory_rules?.scope || "conversation"}
                                onChange={(e) => setMem({ scope: e.target.value })}
                                data-testid="skill-memory-scope"
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white h-10 px-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            >
                                <option value="conversation">This conversation</option>
                                <option value="matter">Matter (legal)</option>
                                <option value="user">Requesting user</option>
                                <option value="company">Whole company</option>
                            </select>
                        </Field>
                    </div>
                )}
                <label className="mt-3 inline-flex items-center gap-2 text-[13px] text-gray-800" data-testid="skill-privileged-toggle">
                    <input
                        type="checkbox"
                        checked={form.memory_rules?.is_privileged}
                        onChange={(e) => setMem({ is_privileged: e.target.checked })}
                    />
                    Legal/privileged work — inherit matter privilege + matter isolation
                </label>
            </div>
        </div>
    );
}

function StepTest({ form, testInputs, setTestInputs, testOutput, testRunning, onRun, onSaveExample }) {
    return (
        <div className="space-y-4" data-testid="step-test">
            <div>
                <h2 className="atlas-text-gradient text-[26px] font-semibold tracking-tight">Test</h2>
                <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Run the skill against sample inputs. No memory is written and no external actions execute — this is a sandbox.
                </p>
            </div>
            <div className="wfos-card p-4 space-y-3" data-testid="skill-test-form">
                {(form.required_inputs || []).length === 0 && (
                    <div className="text-[13px] text-gray-500">Add at least one input on Step 3 to run a test.</div>
                )}
                {(form.required_inputs || []).map((inp) => (
                    <Field key={inp.key} label={inp.label || inp.key} required={inp.required !== false}>
                        {inp.type === "text" ? (
                            <textarea
                                rows={3}
                                value={testInputs[inp.key] || ""}
                                onChange={(e) => setTestInputs({ ...testInputs, [inp.key]: e.target.value })}
                                data-testid={`test-input-${inp.key}`}
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white p-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-[#16a34a] resize-y"
                            />
                        ) : inp.type === "select" ? (
                            <select
                                value={testInputs[inp.key] || ""}
                                onChange={(e) => setTestInputs({ ...testInputs, [inp.key]: e.target.value })}
                                data-testid={`test-input-${inp.key}`}
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white h-10 px-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            >
                                <option value="">—</option>
                                {(inp.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                        ) : (
                            <input
                                type={inp.type === "number" ? "number" : (inp.type === "date" ? "date" : "text")}
                                value={testInputs[inp.key] || ""}
                                onChange={(e) => setTestInputs({ ...testInputs, [inp.key]: e.target.value })}
                                data-testid={`test-input-${inp.key}`}
                                className="w-full rounded-lg border border-[#e8e8e3] bg-white h-10 px-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                            />
                        )}
                    </Field>
                ))}
                <div className="flex items-center gap-2">
                    <button
                        onClick={onRun}
                        disabled={testRunning || (form.required_inputs || []).length === 0}
                        data-testid="skill-test-run"
                        className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-4 h-10 text-[13px]"
                    >
                        <Play className="w-4 h-4" strokeWidth={2} /> {testRunning ? "Running…" : "Run test"}
                    </button>
                    {testOutput && (
                        <button
                            onClick={onSaveExample}
                            data-testid="skill-test-save-example"
                            className="atlas-btn-ghost inline-flex items-center gap-1 rounded-lg px-3 h-10 text-[13px]"
                        >
                            <ClipboardCopy className="w-3.5 h-3.5" strokeWidth={2} /> Save as example
                        </button>
                    )}
                </div>
            </div>
            {testOutput && (
                <div className="wfos-card p-4" data-testid="skill-test-output">
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500 mb-2">Output</div>
                    <div className="whitespace-pre-wrap text-[13px] text-gray-900 leading-relaxed max-h-[420px] overflow-y-auto wfos-scrollbar">
                        {testOutput}
                    </div>
                </div>
            )}
            <div className="text-[11.5px] text-gray-500 flex items-center gap-1">
                <AlertTriangle className="w-3 h-3 text-amber-500" strokeWidth={2} />
                Test runs are sandboxed. No memory is written. No emails, filings, or payments happen.
            </div>
        </div>
    );
}

function StepPublish({ form, set, onSave, saving }) {
    return (
        <div className="space-y-4" data-testid="step-publish">
            <div>
                <h2 className="atlas-text-gradient text-[26px] font-semibold tracking-tight">Publish</h2>
                <p className="mt-1 text-[13.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                    Choose who can see and install this skill. You can change visibility later.
                </p>
            </div>
            <div className="wfos-card p-4 space-y-2" data-testid="skill-publish-visibility">
                {[
                    { v: "private", label: "Private", d: "Only you (and admins) see this skill." },
                    { v: "team",    label: "Team",    d: "Anyone in your workspace can install it." },
                    { v: "public_marketplace", label: "Public Marketplace", d: "Submit for Nexus One review. Optionally charge a monthly price." },
                ].map((opt) => (
                    <label key={opt.v} className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${form.visibility === opt.v ? "border-[#16a34a] bg-[#f0fdf4]" : "border-[#e8e8e3] bg-white hover:border-[#c7c7bd]"}`}>
                        <input
                            type="radio"
                            name="visibility"
                            checked={form.visibility === opt.v}
                            onChange={() => set({ visibility: opt.v })}
                            data-testid={`skill-visibility-${opt.v}`}
                            className="mt-0.5"
                        />
                        <div>
                            <div className="text-[13px] font-medium text-gray-900">{opt.label}</div>
                            <div className="text-[12px] text-gray-500">{opt.d}</div>
                        </div>
                    </label>
                ))}
            </div>
            {form.visibility === "public_marketplace" && (
                <div className="wfos-card p-4">
                    <Field label="Monthly price for other tenants (USD, optional — leave blank for free)">
                        <input
                            type="number"
                            min={0}
                            step={1}
                            value={form.monthly_price_if_public || ""}
                            onChange={(e) => set({ monthly_price_if_public: e.target.value ? Number(e.target.value) : null })}
                            data-testid="skill-marketplace-price"
                            className="w-40 rounded-lg border border-[#e8e8e3] bg-white h-10 px-3 text-[13.5px] focus:outline-none focus:ring-1 focus:ring-[#16a34a]"
                        />
                    </Field>
                    <div className="mt-2 text-[11.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-2.5">
                        <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" strokeWidth={2} />
                        By publishing to the marketplace, you agree to be reviewed by Nexus One. Your skill will appear in Marketplace only after platform-owner approval.
                    </div>
                </div>
            )}
        </div>
    );
}
